import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import type { RootState } from "store";

import CloseButton from "components/layout/CloseButton";
import FunctionalButton from "components/layout/FunctionalButton";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import {
  EmailTemplate,
  createEmailTemplate,
  updateEmailTemplate,
  type CreateEmailTemplateInput,
  type UpdateEmailTemplateInput,
} from "lib/mission/emailNotificationsApi";

const fieldClass =
  "w-full rounded-r5 border border-black80 bg-black70 px-3 py-2.5 text-h5 text-white placeholder:text-black60 focus:border-primary2 focus:outline-none";

interface EmailTemplateDialogProps {
  visible: boolean;
  initial?: EmailTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}

const EmailTemplateDialog: React.FC<EmailTemplateDialogProps> = ({
  visible,
  initial,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation(["settings", "common"]);
  const { arcId, arcProxyHost } = useSelector((state: RootState) => state.arcProxy);
  const [name, setName] = useState("");
  const [subjectTemplate, setSubjectTemplate] = useState("");
  const [bodyTemplate, setBodyTemplate] = useState("");
  const [bodyFormat, setBodyFormat] = useState<"markdown" | "html" | "text">("markdown");
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    if (visible) {
      setName(initial?.name ?? "");
      setSubjectTemplate(initial?.subjectTemplate ?? "");
      setBodyTemplate(initial?.bodyTemplate ?? "");
      setBodyFormat(initial?.bodyFormat ?? "markdown");
      setEnabled(initial?.enabled ?? true);
      setAdvancedOpen(false);
      setSaving(false);
      setError(null);
    }
  }, [visible, initial]);

  const handleSubmit = useCallback(async () => {
    const trimmedName = name.trim();
    const trimmedSubject = subjectTemplate.trim();
    const trimmedBody = bodyTemplate.trim();

    if (!trimmedName) {
      setError(t("emailNotifications.nameRequired"));
      return;
    }
    if (!trimmedSubject) {
      setError(t("emailNotifications.subjectRequired"));
      return;
    }
    if (!trimmedBody) {
      setError(t("emailNotifications.bodyRequired"));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (initial) {
        const input: UpdateEmailTemplateInput = {
          name: trimmedName,
          subjectTemplate: trimmedSubject,
          bodyTemplate: trimmedBody,
          bodyFormat,
          enabled,
        };
        await updateEmailTemplate(initial.id, input, arcProxyHost, arcId);
      } else {
        const input: CreateEmailTemplateInput = {
          name: trimmedName,
          subjectTemplate: trimmedSubject,
          bodyTemplate: trimmedBody,
          bodyFormat,
          enabled,
        };
        await createEmailTemplate(input, arcProxyHost, arcId);
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("emailNotifications.saveFailed"));
    } finally {
      setSaving(false);
    }
  }, [
    name,
    subjectTemplate,
    bodyTemplate,
    bodyFormat,
    enabled,
    initial,
    onSaved,
    onClose,
    arcProxyHost,
    arcId,
    t,
  ]);

  const isEdit = Boolean(initial);

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      className="max-h-[min(90vh,40rem)] w-full max-w-lg overflow-y-auto rounded-r10 border border-black80 bg-black90 p-4"
      zIndexClassName="z-[10050]"
    >
      <div className="mb-4 flex items-center justify-between">
        <p className="text-h4 font-semibold text-white">
          {isEdit
            ? t("emailNotifications.templateDialogEdit")
            : t("emailNotifications.templateDialogNew")}
        </p>
        <CloseButton onClick={onClose} className="relative shrink-0" />
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-h5 text-white">{t("emailNotifications.templateName")}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("emailNotifications.templateNamePlaceholder")}
            className={fieldClass}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-h5 text-white">{t("emailNotifications.templateSubject")}</label>
          <input
            type="text"
            value={subjectTemplate}
            onChange={(e) => setSubjectTemplate(e.target.value)}
            placeholder={t("emailNotifications.templateSubjectPlaceholder")}
            className={fieldClass}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-h5 text-white">{t("emailNotifications.templateBody")}</label>
          <textarea
            rows={8}
            value={bodyTemplate}
            onChange={(e) => setBodyTemplate(e.target.value)}
            placeholder={t("emailNotifications.templateBodyPlaceholder")}
            className={`${fieldClass} resize-y font-mono`}
          />
        </div>

        <label className="flex items-center gap-2 text-h5 text-white">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-5 w-5 rounded accent-primary2"
          />
          {t("emailNotifications.templateEnabled")}
        </label>

        <div className="border-t border-black80 pt-3">
          <button
            type="button"
            className="flex w-full items-center justify-between text-left text-h5 font-semibold text-white"
            onClick={() => setAdvancedOpen((o) => !o)}
            aria-expanded={advancedOpen}
          >
            <span>{t("emailNotifications.advancedSettings")}</span>
            <span className="text-h5 text-black40">{advancedOpen ? "▾" : "▸"}</span>
          </button>

          {advancedOpen ? (
            <div className="mt-3 flex flex-col gap-3">
              <p className="text-h5 text-black60">{t("emailNotifications.templateBodyHint")}</p>
              <div>
                <p className="mb-1.5 text-h5 text-white">{t("emailNotifications.templateFormat")}</p>
                <div
                  className="flex w-full max-w-md gap-1 rounded-r5 border border-black80 bg-black70 p-1"
                  role="tablist"
                  aria-label={t("emailNotifications.templateFormat")}
                >
                  {(
                    [
                      ["markdown", "templateFormatMarkdown"],
                      ["html", "templateFormatHtml"],
                      ["text", "templateFormatText"],
                    ] as const
                  ).map(([value, labelKey]) => (
                    <button
                      key={value}
                      type="button"
                      role="tab"
                      aria-selected={bodyFormat === value}
                      onClick={() => setBodyFormat(value)}
                      className={`flex-1 rounded-r3 px-3 py-2 text-h5 ${
                        bodyFormat === value
                          ? "bg-black80 font-semibold text-white"
                          : "text-black40 hover:bg-black80/60 hover:text-white"
                      }`}
                    >
                      {t(`emailNotifications.${labelKey}`)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {error ? (
        <p className="mt-3 text-h5 text-red-400" role="alert">
          {error}
        </p>
      ) : null}

      <div className="mt-4 flex items-center justify-end gap-2">
        <FunctionalButton
          className="h-8 min-h-8 rounded-r5 border border-black80 bg-black70 px-3 hover:bg-black80 disabled:opacity-50"
          title={t("common:cancel")}
          titleClassName="!normal-case !text-h5 !leading-none !font-normal !text-white"
          onClick={onClose}
          disabled={saving}
        />
        <FunctionalButton
          className="h-8 min-h-8 rounded-r5 border border-primary2 bg-primary2 px-3 hover:bg-primary1 disabled:opacity-50"
          title={
            saving
              ? t("common:saving")
              : isEdit
                ? t("emailNotifications.saveTemplate")
                : t("emailNotifications.createTemplate")
          }
          titleClassName="!normal-case !text-h5 !leading-none !font-normal !text-white"
          onClick={() => void handleSubmit()}
          disabled={saving}
        />
      </div>
    </AnimatedModal>
  );
};

export default EmailTemplateDialog;
